import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/logo";
import { seoMeta } from "@/lib/seo/head";
import { Loader2, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  head: () =>
    seoMeta({
      title: "Imposta nuova password",
      description: "Imposta una nuova password per il tuo account LexAIPro",
      path: "/reset-password",
      noindex: true,
    }),
  component: ResetPasswordPage,
});

type Status = "verifying" | "ready" | "invalid";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("verifying");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  // simple anti-bruteforce: limit attempts per page load
  const attemptsRef = useRef(0);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    let resolved = false;
    const finish = (s: Status) => {
      if (resolved) return;
      resolved = true;
      setStatus(s);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        finish("ready");
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) finish("ready");
    });

    // After 4s without a recovery session, mark as invalid link
    const timer = setTimeout(() => finish("invalid"), 4000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || locked) return;
    if (password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono.");
      return;
    }
    attemptsRef.current += 1;
    if (attemptsRef.current > 5) {
      setLocked(true);
      toast.error("Troppi tentativi. Ricarica la pagina e riprova.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Password aggiornata. Reindirizzamento...");
    // Short delay so the toast is visible before navigation
    setTimeout(() => navigate({ to: "/app" }), 600);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <Logo variant="light" size={48} />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-serif">Imposta nuova password</h1>
          <p className="text-muted-foreground">
            {status === "ready"
              ? "Scegli una nuova password per il tuo account."
              : status === "verifying"
                ? "Verifica del link di recupero in corso..."
                : "Link non valido o scaduto."}
          </p>
        </div>

        {status === "verifying" && (
          <div className="rounded-lg border bg-card p-8 text-center">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              Stiamo validando il link...
            </p>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nuova password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={loading || locked}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Minimo 8 caratteri.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Conferma password</Label>
              <Input
                id="confirm"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                disabled={loading || locked}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || locked || !password || !confirm}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aggiornamento...
                </>
              ) : locked ? (
                "Bloccato — ricarica"
              ) : (
                <>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Aggiorna password
                </>
              )}
            </Button>
          </form>
        )}

        {status === "invalid" && (
          <div className="rounded-lg border bg-card p-6 text-center text-sm space-y-3">
            <p className="text-muted-foreground">
              Il link non è valido o è scaduto. Richiedi un nuovo link di
              recupero.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block text-primary hover:underline"
            >
              Richiedi nuovo link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
